/**
 * useDocumentEvents - SSE hook for real-time document status updates
 *
 * Replaces polling-based status tracking with Server-Sent Events.
 * Connects to /api/events endpoint and receives push notifications.
 */
import { useEffect, useRef, useCallback } from 'react';

// Event types from the server
export type DocumentEventType =
  | 'job:queued'
  | 'job:step_queued'
  | 'job:step_started'
  | 'job:completed'
  | 'job:failed'
  | 'document:created'
  | 'document:updated'
  | 'archive:queued'
  | 'archive:started'
  | 'archive:progress'
  | 'archive:ready'
  | 'archive:failed'
  | 'archive:cancelled';

export interface DocumentEvent {
  type: DocumentEventType;
  doc_id: string;
  step?: string;
  filename?: string;
  error?: string;
  parent_doc_id?: string;
  retry_attempt?: number;
  processing_status?: string;  // Human-readable status from worker (single source of truth)
  [key: string]: unknown;
}

export interface UseDocumentEventsOptions {
  onEvent?: (event: DocumentEvent) => void;
  onStepQueued?: (docId: string, step: string) => void;
  onStepStarted?: (docId: string, step: string, retryAttempt?: number, processingStatus?: string) => void;
  onCompleted?: (docId: string) => void;
  onFailed?: (docId: string, error: string) => void;
  onDocumentCreated?: (docId: string, filename: string, parentDocId?: string) => void;
  onDocumentUpdated?: (docId: string, data: Record<string, unknown>) => void;
  onConnectionChange?: (connected: boolean) => void;
  enabled?: boolean;
}

export function useDocumentEvents(options: UseDocumentEventsOptions = {}) {
  const {
    onEvent,
    onStepQueued,
    onStepStarted,
    onCompleted,
    onFailed,
    onDocumentCreated,
    onDocumentUpdated,
    onConnectionChange,
    enabled = true,
  } = options;

  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttempts = useRef(0);

  // Store callbacks in refs to avoid reconnection on callback changes
  const callbacksRef = useRef({
    onEvent,
    onStepQueued,
    onStepStarted,
    onCompleted,
    onFailed,
    onDocumentCreated,
    onDocumentUpdated,
    onConnectionChange,
  });

  // Update refs when callbacks change
  useEffect(() => {
    callbacksRef.current = {
      onEvent,
      onStepQueued,
      onStepStarted,
      onCompleted,
      onFailed,
      onDocumentCreated,
      onDocumentUpdated,
      onConnectionChange,
    };
  }, [onEvent, onStepQueued, onStepStarted, onCompleted, onFailed, onDocumentCreated, onDocumentUpdated, onConnectionChange]);

  const handleEvent = useCallback((eventType: string, data: DocumentEvent) => {
    const callbacks = callbacksRef.current;

    // Call generic handler first
    callbacks.onEvent?.(data);

    // Call specific handlers
    switch (eventType) {
      case 'job:step_queued':
        if (data.doc_id && data.step) {
          callbacks.onStepQueued?.(data.doc_id, data.step);
        }
        break;
      case 'job:step_started':
        if (data.doc_id && data.step) {
          callbacks.onStepStarted?.(data.doc_id, data.step, data.retry_attempt, data.processing_status);
        }
        break;
      case 'job:completed':
        if (data.doc_id) {
          callbacks.onCompleted?.(data.doc_id);
        }
        break;
      case 'job:failed':
        if (data.doc_id) {
          callbacks.onFailed?.(data.doc_id, data.error || 'Unknown error');
        }
        break;
      case 'document:created':
        if (data.doc_id && data.filename) {
          callbacks.onDocumentCreated?.(data.doc_id, data.filename, data.parent_doc_id);
        }
        break;
      case 'document:updated':
        if (data.doc_id) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { type, doc_id, ...rest } = data;
          callbacks.onDocumentUpdated?.(data.doc_id, rest);
        }
        break;
    }
  }, []);

  const connect = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    // Create EventSource with credentials for session cookie
    const eventSource = new EventSource('/api/events', {
      withCredentials: true,
    });

    eventSource.onopen = () => {
      console.log('[SSE] Connected to event stream');
      reconnectAttempts.current = 0;
      callbacksRef.current.onConnectionChange?.(true);
    };

    eventSource.onerror = (error) => {
      console.error('[SSE] Connection error:', error);
      callbacksRef.current.onConnectionChange?.(false);

      // EventSource auto-reconnects, but we track state
      if (eventSource.readyState === EventSource.CLOSED) {
        // Connection closed, schedule reconnect with backoff
        const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
        reconnectAttempts.current++;

        console.log(`[SSE] Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current})`);

        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, delay);
      }
    };

    // Listen for specific event types
    const eventTypes: DocumentEventType[] = [
      'job:queued',
      'job:step_queued',
      'job:step_started',
      'job:completed',
      'job:failed',
      'document:created',
      'document:updated',
      'archive:queued',
      'archive:started',
      'archive:progress',
      'archive:ready',
      'archive:failed',
      'archive:cancelled',
    ];

    eventTypes.forEach((eventType) => {
      eventSource.addEventListener(eventType, (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data) as DocumentEvent;
          handleEvent(eventType, data);
        } catch (e) {
          console.error('[SSE] Failed to parse event data:', e);
        }
      });
    });

    // Handle connection confirmation
    eventSource.addEventListener('connected', (event: MessageEvent) => {
      console.log('[SSE] Connection confirmed:', event.data);
    });

    // Handle errors from server (note: event.data may be undefined for connection errors)
    eventSource.addEventListener('error', (event: MessageEvent) => {
      if (event.data) {
        console.error('[SSE] Server error:', event.data);
      }
      // Connection errors (like ERR_HTTP2_PROTOCOL_ERROR) don't have data
      // and are already handled by the onerror handler above
    });

    eventSourceRef.current = eventSource;
  }, [handleEvent]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (enabled) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      disconnect();
    };
    // Only reconnect when enabled changes, not on every callback change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return {
    disconnect,
    reconnect: connect,
  };
}
